import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import { CruiseSaleLiteModel, useGetCruiseSales } from 'api-hooks/sale/cruise';
import { CheckCircleFillSVG } from 'common/assets';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import stylesColors from 'common/styles/styles.colors';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import {
  commonColumnTable,
  commonSaleColumnTable,
} from 'legacy-modules/common/column';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import CruiseSaleTable from './components/table';
import {
  BillingAddressTagname,
  BookingCodesTagname,
  CustomerListTagname,
} from '../component/list-tagname';
import SaleListTabs from '../component/sale-list-tabs';
import SalePillStatus from '../component/status';

export default function CruiseSaleList() {
  const { t } = useTranslation();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const formatDate = useDate();

  const _columns = React.useMemo<IColumn<CruiseSaleLiteModel>[]>(
    () => [
      {
        header: t('common:invoice_status'),
        accessorKey: 'status',
        minSize: 150,
        sortName: 'status',
        cell: ({ getValue }) => <SalePillStatus value={getValue() as string} />,
      },
      {
        header: t('common:payment_status'),
        accessorKey: 'paymentStatus',
        minSize: 150,
        sortName: 'payment_status',
        cell: ({ getValue }) => <SalePillStatus value={getValue() as string} />,
      },
      {
        header: t('common:complete'),
        accessorKey: 'isCompleted',
        sortName: 'is_completed',
        minSize: 150,
        cell: ({ row }) => {
          const {
            original: { isCompleted },
          } = row;
          if (!isCompleted) {
            return null;
          }
          return <CheckCircleFillSVG color={stylesColors.primaryDefault} />;
        },
      },
      {
        header: t('common:transaction_at'),
        accessorKey: 'transactionAt',
        minSize: 150,
        sortName: 'transaction_at',
        cell: ({ getValue }) => formatDate(getValue() as Date),
      },
      {
        header: t('common:invoice_number'),
        accessorKey: 'invoiceNumber',
        minSize: 150,
        sortName: 'invoice_number',
      },
      {
        header: t('common:customer'),
        accessorKey: 'customer',
        minSize: 150,
        cell: ({ row }) => {
          const customer = row.original.customer;
          return <CustomerListTagname customer={customer} />;
        },
      },
      {
        header: t('common:group_code'),
        accessorKey: 'code',
        minSize: 150,
        sortName: 'code',
      },
      {
        header: t('common:project'),
        accessorKey: 'project',
        minSize: 150,
        cell: ({ row }) => row.original.project?.code || '-',
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 150,
        sortName: 'description',
      },
      {
        header: t('common:total_sales'),
        accessorKey: 'total',
        minSize: 150,
        textAlign: 'right',
        sortName: 'total',
        cell: ({ getValue }) => string2money(getValue() as number),
      },
      {
        header: t('common:billing_address'),
        accessorKey: 'billingAddress',
        minSize: 150,
        cell: ({ row }) => {
          const billingAddress = row.original.billingAddress;
          return <BillingAddressTagname billingAddress={billingAddress} />;
        },
      },
      {
        header: t('common:guest'),
        accessorKey: 'guests',
        minSize: 150,
        cell: ({ getValue }) =>
          (getValue() as any).map((guest) => guest).join(', '),
      },
      ...commonSaleColumnTable(t),
      {
        header: t('common:booking_code'),
        accessorKey: 'bookingCodes',
        minSize: 150,
        cell: ({ row }) => {
          const bookingCodes = row.original.bookingCodes;
          return <BookingCodesTagname bookingCodes={bookingCodes} />;
        },
      },
      ...commonColumnTable(t),
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetCruiseSales,
    {
      params: {
        page,
        filter: activeKey === '6' ? { payment_status: status } : { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.CruiseSalesPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('cruise-sales');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:cruise_sale')}
          showAdd={permission.create}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          linkAdd={NavigationRoutes.CruiseSalesCreate}
        />
        <SaleListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              refetchNotification();
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />

        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <CruiseSaleTable
          cruiseSales={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
